// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  LayoutGrid,
  ExternalLink,
  PiggyBank,
  BadgeIndianRupee,
  Shield,
} from 'lucide-react';

// ── App registry ──────────────────────────────────────────────────
const APPS = [
  {
    id: 'budget',
    name: 'Budget.io',
    description: 'Track expenses, wallets and spending trends.',
    icon: <BadgeIndianRupee className="w-8 h-8 text-blue-500" />,
    to: 'budget',
    category: 'Productivity',
  },
  {
    id: 'budgetfeedio',
    name: 'BudgetFeed.io',
    description: 'Import bank statements and classify transactions.',
    icon: <PiggyBank className="w-8 h-8 text-blue-400" />,
    to: 'budgetfeed',
    category: 'Productivity',
  },
  {
    id: 'security',
    name: 'Security Center',
    description: 'Monitor user permissions and access logs.',
    icon: <Shield className="w-8 h-8 text-red-500" />,
    to: 'security',
    category: 'Security',
  },
  {
    id: 'admin',
    name: 'Project Board',
    description: 'Task management and team collaboration.',
    icon: <LayoutGrid className="w-8 h-8 text-purple-500" />,
    to: 'admin',
    category: 'Admin',
  },
];

// ── App selection dashboard ───────────────────────────────────────
export function AppDashboard() {
  const [search, setSearch] = useState('');

  const filtered = APPS.filter(
    (app) =>
      app.name.toLowerCase().includes(search.toLowerCase()) ||
      app.description.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-12">
      {/* Header */}
      <header className="max-w-6xl mx-auto mb-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Welcome Back</h1>
        <p className="text-gray-500 mb-8">
          Select an application to get started.
        </p>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search applications…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
          />
        </div>
      </header>

      {/* Grid */}
      <main className="max-w-6xl mx-auto">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Your Apps</h2>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg">No apps match your search.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((app) => (
              <Link
                key={app.id}
                to={app.to}
                className="group relative bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="p-3 bg-gray-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                    {app.icon}
                  </div>
                  <ExternalLink className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors" />
                </div>

                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  {app.name}
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">
                  {app.description}
                </p>

                <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-600">
                  {app.category}
                </span>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
